import { useState } from 'react';
import { cssVar } from '@going-once/tokens';
import { focusRing, Text } from './primitives';

export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * **One choice from a few, always visible** (the design system's `RadioGroup`): a `<fieldset>` with a
 * `<legend>` and native radios sharing a `name`, so Tab enters the group, the arrow keys move within it
 * and a screen reader reads "1 of 4". Use a `Select` when there are too many to show.
 */
export function RadioGroup({
  legend,
  name,
  options,
  value,
  onChange,
  direction = 'column',
  disabled,
}: {
  legend: string;
  name: string;
  options: readonly RadioOption[];
  value: string;
  onChange: (value: string) => void;
  direction?: 'row' | 'column';
  disabled?: boolean;
}) {
  const [focused, setFocused] = useState<string | undefined>(undefined);
  return (
    <fieldset style={{ border: 'none', margin: 0, padding: 0, minWidth: 0 }} disabled={disabled}>
      <legend style={{ padding: 0, marginBottom: cssVar('space.xs') }}>
        <Text step="label" tone="fg.muted">
          {legend}
        </Text>
      </legend>
      <div
        style={{
          display: 'flex',
          flexDirection: direction,
          flexWrap: 'wrap',
          columnGap: cssVar('space.xl'),
          rowGap: cssVar('space.xs'),
        }}
      >
        {options.map((option) => {
          const off = disabled === true || option.disabled === true;
          return (
            <label
              key={option.value}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: cssVar('space.sm'),
                minHeight: cssVar('size.target.min'),
                cursor: off ? 'not-allowed' : 'pointer',
                opacity: off ? 0.5 : 1,
              }}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={option.value === value}
                disabled={off}
                onChange={() => onChange(option.value)}
                onFocus={(event) =>
                  setFocused(event.currentTarget.matches(':focus-visible') ? option.value : undefined)
                }
                onBlur={() => setFocused(undefined)}
                style={{
                  flex: 'none',
                  width: cssVar('space.lg'),
                  height: cssVar('space.lg'),
                  margin: 0,
                  accentColor: cssVar('color.accent.default'),
                  cursor: 'inherit',
                  ...(focused === option.value ? focusRing : {}),
                }}
              />
              <Text>{option.label}</Text>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
